import React, { useState, useEffect } from "react";
import { Plus } from "lucide-react";
import HeaderWithIcon from "../../components/common/HeaderWithIcon";
import DefaultGamePackage from "../../components/Popup/DefaultGamePackage";
import { useAuth } from "../../context/AuthContext";
import { gamesApi } from "../../api/games";
import toast from "react-hot-toast";
import DefaultGamePackageRow from "./DefaultGamePackageRow";
import Loading from "../../assets/images/Loading.png"; 

export default function DefaultGamePackages() {
  const [defaultPackages, setDefaultPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addPackagePopup, setAddPackagePopup] = useState(false);
  const { tokens, user } = useAuth();
  const [allGameSettings, setAllGameSettings] = useState([]);

  // Check if the user is an admin
  const isAdmin = user.roleId === 1;

  // Fetch default packages and game settings
  const fetchDefaultPackages = async () => {
    setLoading(true);
    try {
      const response = await gamesApi.getAllGames(tokens.access.token);
      if (response.status === 200) {
        // Store all game settings for reference
        setAllGameSettings(response.data);
      }

      const gamesResponse = await gamesApi.ownedGames(tokens.access.token);
      if (gamesResponse.status === 200) {
        // Filter for default packages (those marked with isDefaultPackage = true)
        const defaultPackagesList = gamesResponse.data.filter(game => game.isDefaultPackage === true && game.active === true);
        setDefaultPackages(defaultPackagesList);
      }
      
      setLoading(false);
    } catch (error) {
      console.error("Error fetching default game packages:", error);
      toast.error("Failed to load default game packages");
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDefaultPackages();
  }, [tokens.access.token]);

  const handleDeletePackage = async (packageId) => {
    try {
      const response = await gamesApi.deleteGame(tokens.access.token, packageId);
      if (response.status === 204) {
        toast.success("Default package deleted successfully");
        fetchDefaultPackages(); // Refresh the list
      } else {
        toast.error("Failed to delete package");
      }
    } catch (error) {
      console.error("Error deleting package:", error);
      toast.error("Error deleting package");
    }
  };

  const getGameSettingDetails = (gameSettingId) => {
    return allGameSettings.find(setting => setting.id === gameSettingId) || {};
  };

  // Helper to format currency
  const formatCurrency = (value) => {
    return parseFloat(value).toFixed(2);
  };

  return (
    <div className="min-h-screen font-poppins">
      <HeaderWithIcon title="Default Game Packages" />
      
      <div className="px-2 py-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-base font-semibold text-[#12131A]">Default Game Packages for Players</h2>
        </div>
        
        <div className="flex justify-between items-center mb-4">            
          {isAdmin && (
            <button
              className="px-3 py-2 bg-[#0E64D1] flex justify-center items-center gap-2 text-sm text-white rounded-md font-medium hover:bg-blue-700 transition-colors"
              onClick={() => setAddPackagePopup(true)}
            >
              <Plus className="w-4 h-4" />
              Add Package
            </button>
          )}
        </div>
        
        <p className="text-sm text-gray-600 mb-4">
          Create and manage default game packages that can be assigned to players.
        </p>
        
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <img src={Loading} alt="Loading" className="animate-spin w-12 h-12" />
          </div>
        ) : (
          <div>
            {defaultPackages.length > 0 ? (
              <div className="space-y-2">
                {defaultPackages.map((pkg) => (
                  <DefaultGamePackageRow
                    key={pkg.id}
                    package={pkg}
                    gameSettingDetails={getGameSettingDetails(pkg.gameSettingsId)}
                    onDelete={() => handleDeletePackage(pkg.id)}
                    refreshPackages={fetchDefaultPackages}
                    formatCurrency={formatCurrency}
                    isAdmin={isAdmin}
                  />
                ))}
              </div>
            ) : (
              <div className="bg-gray-50 rounded-lg p-6 text-center">
                <p className="text-gray-500">No default game packages available.</p>
                {isAdmin && (
                  <button
                    className="mt-3 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                    onClick={() => setAddPackagePopup(true)}
                  >
                    Create Your First Package
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Add Default Game Package Popup */}
      <DefaultGamePackage
        isOpen={addPackagePopup}
        onClose={() => setAddPackagePopup(false)}
        refreshGames={fetchDefaultPackages}
      />
    </div>
  );
} 